import type {
    OffersInfo,
    OutflowContext,
    OutflowOfferProps,
    OutflowOffers,
} from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers.types';
import type { Channel } from 'redux-saga';
import { call, take } from 'redux-saga/effects';

export function createOfferProcessor<P extends OutflowOfferProps, O extends string | number | symbol = OutflowOffers>(
    offersInfo: Partial<Record<O, OffersInfo>>
) {
    type OfferKeyArg = O | ((context: OutflowContext) => O[]);

    function* processOutflowOffer(offerKey: O, offerArgs: P, outflowChannel: Channel<boolean>) {
        const isSuccess = yield take(outflowChannel);
        const { check = () => true, offer } = (offersInfo[offerKey] || { check: () => false, offer: null }) as OffersInfo;
        const enabled = yield check(offerArgs);
        if (!!offer && enabled && isSuccess) {
            yield offer(offerArgs);
        } else {
            yield call(outflowChannel.put, true);
        }
    }

    function* processOuflowOfferListCore(outflowOffers: OfferKeyArg[], offerArgs: P, outflowChannel: Channel<boolean>) {
        for (const offer of outflowOffers) {
            if (typeof offer === 'function') {
                /** note-start:
                Чтобы корректно получить пейлоад с предыдущего оффера, нам нужно обратиться к стору,
                только после того, как двинулись по оттоку дальше. Поэтому дожидаемся ответа канала тут,
                а не в обработчике оффера;
                */
                const isSuccess = yield take(outflowChannel);
                outflowChannel.put(isSuccess);
                /** note-end */
                yield* processOuflowOfferListCore(offer(offerArgs.outflowContext), offerArgs, outflowChannel);
            } else {
                yield processOutflowOffer(offer, offerArgs, outflowChannel);
            }
        }
    }

    function* processOuflowOfferList(outflowOffers: OfferKeyArg[], offerArgs: P, outflowChannel: Channel<boolean>) {
        yield offerArgs.continueOutflow();
        yield processOuflowOfferListCore(outflowOffers, offerArgs, outflowChannel);
        yield offerArgs.cancelOutflow();
    }

    return { processOuflowOfferList } as const;
}
